import React, { useEffect, useState } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, CssBaseline, styled } from '@mui/material';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import { backdropImageUrl, useElementRect } from '@paragon/client-tools';
import { isChMachineUser, isG2User, LoginContainer } from '@paragon/login-tools';
import { ParagonHeader } from '@paragon/material';
import { Notifier, NotifierDialog, useSnackbarContainerElement } from '@paragon/notification-tools';
import { getSpinnersToDisplay, SpinnerMessage } from '@paragon/task-spinner';
import { ThemedLogo, UserDropdown } from '@paragon/themes';

import projectsLogoUrl from '../../resources/projects.png';
import startUp from '../store/actions/startUp';
import { getSnackbarClearanceForActionBar } from '../store/selectors/snackbarClearance';
import { RootState } from '../store/slices';
import { snackbarStackRightSet } from '../store/slices/applicationSlice';
import { useAppDispatch } from './hooks';
import { ProjectsPage } from './ProjectsPage';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': { color: 'white' },
}));

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

function RootComponent(props: Props) {
  const dispatch = useAppDispatch();

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const snackbarContainerRect = useElementRect(useSnackbarContainerElement(rootEl));
  const snackbarStackRight = snackbarContainerRect ? Math.round(snackbarContainerRect.right) : null;
  useEffect(() => {
    if (snackbarStackRight !== null) dispatch(snackbarStackRightSet(snackbarStackRight));
  }, [dispatch, snackbarStackRight]);

  return (
    <Box
      ref={setRootEl}
      sx={{
        flexGrow: 1,
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'fixed',
        top: 0,
        backgroundImage: `url(${backdropImageUrl})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundAttachment: 'fixed',
        '& .notistack-SnackbarContainer': { bottom: props.snackbarClearanceForActionBar ?? undefined },
      }}
    >
      <CssBaseline />

      <SnackbarProvider
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        maxSnack={4}
        Components={{
          default: StyledMaterialDesignContent,
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
          warning: StyledMaterialDesignContent,
          info: StyledMaterialDesignContent,
        }}
      >
        <LoginContainer
          onLogIn={props.startUp}
          logoOverride={(props.useG2Theme && <ThemedLogo width={300} />) || undefined}
          clientLogoUrl={projectsLogoUrl}
          clientName="Projects"
          requirePermission="projects-access"
        >
          <Notifier />
          <NotifierDialog />
          <ParagonHeader
            clientLogoUrl={projectsLogoUrl}
            clientName="paragon-projects"
            text={props.useCHTheme ? '' : 'PARAGON PROJECTS'}
          >
            <UserDropdown />
          </ParagonHeader>
          {Array.from(props.spinnerMessages).map((text) => (
            <SpinnerMessage text={text} key={text} />
          ))}
          <ProjectsPage />
        </LoginContainer>
      </SnackbarProvider>
    </Box>
  );
}

const mapStateToProps = (state: RootState) => ({
  spinnerMessages: getSpinnersToDisplay(state),
  snackbarClearanceForActionBar: getSnackbarClearanceForActionBar(state),
  useG2Theme: isG2User(state),
  useCHTheme: isChMachineUser(state),
});

const actionCreators = {
  startUp,
};

export default connect(mapStateToProps, actionCreators)(RootComponent);
